import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listPolyRealizedViaRelay,
  type PolyRelayRealizedBucket,
  type PolyRelayUnavailableCode,
  type PolyReadCredentialContext,
} from '@/lib/polymarket/relay';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

export type PolyRelayRealizedStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface UsePolymarketRelayRealizedOptions {
  enabled?: boolean;
  pollMs?: number;
}

export interface UsePolymarketRelayRealizedReturn {
  buckets: PolyRelayRealizedBucket[];
  status: PolyRelayRealizedStatus;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  credentialContext: PolyReadCredentialContext;
  lastFetchMs: number | null;
  refresh: () => Promise<void>;
}

// Settled buckets move on settlement cadence (minutes), not per tick — and
// off the serve path each fetch is a lifetime-ticker CLOB scan + full fills
// fold on the relay. 10s polling replayed the Jul-2 read-fire pattern
// (Jul-14 audit); the relay also TTL-caches per user as a second layer.
const DEFAULT_POLL_MS = 120_000;

/** Fetch-on-mount + slow visibility-aware poll of the settlement-aware
 *  ledger used by Polymarket My Trades. `refresh()` is exposed for an
 *  explicit user-driven update. */
export function usePolymarketRelayRealized(
  options: UsePolymarketRelayRealizedOptions = {},
): UsePolymarketRelayRealizedReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const [buckets, setBuckets] = useState<PolyRelayRealizedBucket[]>([]);
  const [status, setStatus] = useState<PolyRelayRealizedStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [unavailableCode, setUnavailableCode] = useState<PolyRelayUnavailableCode | null>(null);
  const [credentialContext, setCredentialContext] = useState<PolyReadCredentialContext>(null);
  const [lastFetchMs, setLastFetchMs] = useState<number | null>(null);
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus((current) => (current === 'idle' ? 'loading' : current));
    try {
      const out = await listPolyRealizedViaRelay();
      setLastFetchMs(Date.now());
      if (out.ok) {
        setBuckets(out.buckets);
        setError(null);
        setUnavailableCode(null);
        setCredentialContext(
          out.credentialContext === undefined
            ? 'CONTEXT_CHECK_FAILED'
            : out.credentialContext,
        );
        setStatus('ready');
      } else if (out.code) {
        setBuckets([]);
        setError(out.error);
        setUnavailableCode(out.code);
        setCredentialContext(out.code);
        setStatus('unavailable');
      } else {
        setError(out.error);
        setUnavailableCode(null);
        setStatus('error');
      }
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void refresh();
    const stopInterval = startVisibilityAwareInterval(() => {
      setStatus((current) => {
        if (!stopped && current !== 'unavailable') void refresh();
        return current;
      });
    }, pollMs);
    return () => {
      stopped = true;
      stopInterval();
    };
  }, [enabled, pollMs, refresh]);

  return { buckets, status, error, unavailableCode, credentialContext, lastFetchMs, refresh };
}
